import React, { useCallback } from 'react';
import {
  type BackdropProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  type DialogTitleProps,
  type PaperProps,
  IconButton,
} from '@material-ui/core';
import type { TransitionProps } from '@material-ui/core/transitions';
import { Icon, type IconProps } from '@popmenu/common-ui';
import { X as XIcon } from '@popmenu/web-icons';

import { executeWithProgressBar } from '~/utils/postponed';
import { slideComponent } from '../../shared/TransitionComponent';
import { useWindowSizeContext } from '../../shared/WindowSizeProvider';
import { classNames, DarkThemeProvider, makeStyles } from '../../utils/withStyles';
import modalStyles from '../../assets/jss/shared/modalStyles';

import { toSnakeCase } from '../../utils/strings';
import { useCurrentSession } from '../../shared/CurrentSessionProvider';

const LeftTransition = slideComponent('left', 'LeftTransition');

const UpTransition = slideComponent('up', 'UpTransition');

const TRANSITION_COMPONENTS = {
  left: LeftTransition,
  popover: null,
  up: UpTransition,
};

type Transition = keyof typeof TRANSITION_COMPONENTS;

const transitionComponentProps = (transition: Transition | undefined) => {
  if (!transition) {
    return null;
  }

  const componentTransition = TRANSITION_COMPONENTS[transition];
  if (componentTransition) {
    return {
      TransitionComponent: componentTransition,
    };
  }
  return null;
};

const useStyles = makeStyles(modalStyles);

export type BasicModalProps = {
  actions?: React.ReactNode,
  actionsClassName?: string,
  autoFocusCloseButton?: boolean,
  BackdropProps?: Partial<BackdropProps>,
  backgroundColor?: string,
  centerTitle?: boolean,
  children?: React.ReactNode | (() => React.ReactNode),
  className?: string,
  closeButton?: boolean,
  // TODO: sc-79132 closeModal should be required unless closeButton is definitely false
  closeModal?: () => void,
  contentClassName?: string,
  dark?: boolean,
  DialogContentProps?: object,
  DialogProps?: object,
  DialogTitleProps?: DialogTitleProps,
  dialogTitleStyles?: string,
  disableBackdropClick?: boolean,
  disableEnforceFocus?: boolean,
  disableEscapeKeyDown?: boolean,
  disablePortal?: boolean,
  disableTransition?: boolean,
  fullScreen?: boolean,
  fullScreenMobile?: boolean,
  hidden?: boolean,
  hideBackdrop?: boolean,
  icon?: IconProps['icon'],
  mobileSmall?: boolean,
  paper?: boolean,
  paperClass?: classNames.Argument,
  PaperProps?: Partial<PaperProps>,
  scroll?: 'body' | 'paper',
  show: boolean,
  size?: 'lg' | 'md' | 'sm' | 'xl',
  style?: React.CSSProperties,
  title?: MaybeFormattedMessage,
  transition?: Transition,
  transitionDelay?: number,
  transitionDuration?: number,
};

const BasicModal = (props: BasicModalProps) => {
  const {
    actions = null,
    actionsClassName,
    autoFocusCloseButton = false,
    BackdropProps = {},
    centerTitle = false,
    children = null,
    className,
    closeButton = true,
    closeModal = () => {
      // do nothing
    },
    contentClassName,
    dark = false,
    DialogContentProps = {},
    DialogProps = {},
    DialogTitleProps = {},
    dialogTitleStyles,
    disableBackdropClick = true,
    disableEnforceFocus = false,
    disableEscapeKeyDown = false,
    disablePortal = false,
    disableTransition = false,
    fullScreenMobile = false,
    hidden = false,
    hideBackdrop = false,
    icon,
    mobileSmall = false,
    paper = true,
    paperClass,
    PaperProps = {},
    scroll = 'body',
    show,
    size = 'lg',
    style = {},
    title,
    transition = 'up',
    transitionDelay = 0,
    transitionDuration,
  } = props;

  const { isMobile } = useWindowSizeContext();
  const { backgroundColor, fullScreen = false } = isMobile && fullScreenMobile ?
    { backgroundColor: undefined, fullScreen: true } :
    props;

  const classes = useStyles({ backgroundColor });

  const session = useCurrentSession();
  const isThemeDark = session.user?.adminPaletteType === 'admin_palette_dark';
  let transitionProps: TransitionProps & { role: string } = {
    role: 'presentation',
    timeout: disableTransition ? 0 : transitionDuration || undefined,
  };

  if (transitionDelay) {
    transitionProps = {
      ...transitionProps,
      style: {
        transitionDelay: `${transitionDelay}ms`,
      },
    };
  }

  const onClose = useCallback((_event: unknown, reason: string) => {
    if (disableBackdropClick && reason === 'backdropClick') {
      return;
    }
    if (typeof closeModal === 'function') {
      closeModal();
    }
  }, [closeModal, disableBackdropClick]);

  let titleId;
  if (title) {
    titleId = typeof title === 'string' ? `${toSnakeCase(title).replaceAll('_', '-')}-dialog-title` : 'dialog-title';
  }

  const childrenContent = typeof children === 'function' ? children() : children;

  return (
    <Dialog
      disableAutoFocus={autoFocusCloseButton}
      hidden={hidden}
      hideBackdrop={hideBackdrop}
      aria-labelledby={titleId}
      style={style}
      classes={{
        paper: classNames(
          classes[fullScreen ? 'modalPaperFullscreen' : `modal-paper-${size}` as const],
          dark ? classes.modalPaperDark : null,
          paper ? null : classes.modalPaperHidden,
          'pm-modal-paper',
          className,
          backgroundColor ? classes.modalPaperNoBackground : null,
          mobileSmall ? classes.modalPaperMobileSmall : null,
          paperClass,
        ),
        root: classNames(
          classes.modalRoot,
          'pm-modal',
        ),
        scrollBody: classNames(
          className,
          classes.modalScrollBody,
          mobileSmall ?
            classes.modalScrollBodyMobileSmall :
            classes.modalScrollBody,
          (fullScreen || mobileSmall) ?
            classes.modalScrollBodyZeroSpacing : null,
          'pm-modal-body',
        ),
      }}
      BackdropProps={{
        className: classNames(
          classes.modalBackdrop,
          'pm-modal-backdrop',
        ),
        style: {
          opacity: disableTransition ? 1 : undefined,
        },
        transitionDuration: disableTransition ? 0 : transitionDuration || undefined,
        ...BackdropProps,
      }}
      disableEnforceFocus={disableEnforceFocus}
      disableEscapeKeyDown={disableEscapeKeyDown}
      disablePortal={disablePortal}
      fullWidth
      maxWidth={fullScreen ? 'xl' : size}
      onClose={onClose}
      open={show}
      PaperProps={{ ...PaperProps, 'aria-modal': true }}
      scroll={scroll}
      TransitionProps={transitionProps}
      {...transitionComponentProps(transition)}
      {...DialogProps}
    >
      {(title || icon) && (
        <DialogTitle id={titleId} className={classNames(centerTitle ? classes.centeredDialogTitle : classes.dialogTitle, dialogTitleStyles)} {...DialogTitleProps}>
          {icon && <Icon aria-hidden className={classes.dialogTitleIcon} icon={icon} />}
          {title}
        </DialogTitle>
      )}
      <DialogContent
        className={classNames(
          classes.dialogContent,
          contentClassName,
          (fullScreen || mobileSmall) ?
            classes.dialogContentZeroSpacing : null,
        )}
        {...DialogContentProps}
        style={{ overflow: scroll === 'paper' && !fullScreen ? 'hidden auto' : 'visible' }}
      >
        <React.Fragment>
          {closeButton && (
            <div className={scroll === 'paper' ? classes.closeButtonWrapper : undefined}>
              <IconButton
                aria-label="Close Dialog"
                autoFocus={autoFocusCloseButton}
                className={classNames(
                  scroll === 'paper' ? classes.closeButtonPaper : classes.closeButton,
                  dark ? classes.closeButtonDark : null,
                  paper ? null : classes.closeButtonPaperHidden,
                )}
                color="inherit"
                data-cy="close_modal_button"
                data-testid="close_modal_button"
                onClick={(() => executeWithProgressBar(() => closeModal?.()))}
                size="medium"
              >
                <Icon icon={XIcon} />
              </IconButton>
            </div>
          )}
          {dark || isThemeDark ? (
            <DarkThemeProvider>
              {childrenContent}
            </DarkThemeProvider>
          ) : childrenContent}
        </React.Fragment>
      </DialogContent>
      {actions && (
        <DialogActions className={classNames(classes.dialogActions, actionsClassName)}>
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default BasicModal;
